import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { apiClient, useAuth } from "@/lib/api";
import { toast } from "sonner";
import { 
  Layout, 
  Sparkles, 
  Image, 
  Video, 
  X,
  Check,
  ChevronRight,
  Loader2,
  Type,
  Palette,
  ImageIcon,
  User,
  Tag,
  MapPin,
  Star,
  Eye,
  Heart,
  HeartOff
} from "lucide-react";
import { Button } from "@/components/ui/button";

// Template categories with descriptions - Extended to match Smart Detection
const categories = [
  { id: "all", label: "All Templates", count: 500, icon: "🎨" },
  { id: "general", label: "General", count: 25, icon: "📝" },
  { id: "inspiration", label: "Quotes & Inspiration", count: 25, icon: "💭" },
  { id: "motivational", label: "Motivational", count: 25, icon: "🔥" },
  { id: "spiritual", label: "Spiritual / Religious", count: 25, icon: "🙏" },
  { id: "events_celebrations", label: "Events & Celebrations", count: 25, icon: "🎉" },
  { id: "nature", label: "Nature & Travel", count: 25, icon: "🌿" },
  { id: "product", label: "Product / E-commerce", count: 25, icon: "🛍️" },
  { id: "property", label: "Property / Real Estate", count: 25, icon: "🏠" },
  { id: "mortgage", label: "Mortgage & Lending", count: 25, icon: "🏦" },
  { id: "finance", label: "Finance & Investment", count: 25, icon: "💰" },
  { id: "insurance", label: "Insurance", count: 25, icon: "🛡️" },
  { id: "service", label: "Service", count: 25, icon: "💼" },
  { id: "event", label: "Event", count: 25, icon: "📅" },
  { id: "job", label: "Job / Career", count: 25, icon: "👔" },
  { id: "restaurant", label: "Restaurant / Food", count: 25, icon: "🍽️" },
  { id: "automotive", label: "Automotive", count: 25, icon: "🚗" },
  { id: "fitness", label: "Health / Fitness", count: 25, icon: "💪" },
  { id: "healthcare", label: "Healthcare / Medical", count: 25, icon: "🏥" },
  { id: "beauty", label: "Beauty & Wellness", count: 25, icon: "💅" },
  { id: "travel", label: "Travel", count: 25, icon: "✈️" },
  { id: "education", label: "Education", count: 25, icon: "📚" },
  { id: "pet", label: "Pet / Animal", count: 25, icon: "🐾" },
  { id: "rental", label: "Rental", count: 25, icon: "🔑" },
  { id: "testimonial", label: "Testimonials", count: 25, icon: "⭐" },
  { id: "marketing", label: "Marketing", count: 25, icon: "📢" },
  { id: "business", label: "Business", count: 25, icon: "🏢" },
  { id: "ecommerce", label: "E-commerce", count: 25, icon: "🛒" },
  { id: "legal", label: "Legal", count: 25, icon: "⚖️" },
  { id: "construction", label: "Construction", count: 25, icon: "🏗️" },
  { id: "photography", label: "Photography", count: 25, icon: "📷" },
  { id: "wedding", label: "Wedding", count: 25, icon: "💒" }
];

// Template preview component - ULTRA PREMIUM with category-specific sample content
function TemplatePreview({ template, showDetails = false }) {
  const bgStyle = template.background_style?.type === "gradient"
    ? { background: `linear-gradient(135deg, ${template.background_style.colors?.join(", ") || "#1a1a2e, #16213e"})` }
    : { background: template.background_style?.color || "#1a1a2e" };
  
  const textColor = template.text_style?.color || "#ffffff";
  const isLight = template.background_style?.type === "solid" && 
    (template.background_style.color === "#ffffff" || template.background_style.color === "#f8f8f8");
  
  // Get accent color from gradient
  const accentColor = template.background_style?.colors?.[1] || "#667eea";
  const primaryColor = template.background_style?.colors?.[0] || "#1a1a2e";
  
  // Category-specific sample content based on template name
  const getSampleContent = () => {
    const name = (template.name || "").toLowerCase();
    const category = (template.category || "").toLowerCase();
    
    // Quote/Inspiration templates
    if (name.includes("quote") || name.includes("motivation") || name.includes("inspir") || category === "quotes") {
      return { type: "quote", text: "Believe you can and you're halfway there.", author: "T. Roosevelt", cta: null };
    }
    if (name.includes("wisdom") || name.includes("thought")) {
      return { type: "quote", text: "The only way to do great work is to love what you do.", author: "S. Jobs", cta: null };
    }
    
    // NEW: Motivational templates
    if (category === "motivational" || name.includes("motivat") || name.includes("hustle") || name.includes("grind")) {
      return { type: "quote", text: "Success is not final, failure is not fatal.", author: "W. Churchill", cta: null };
    }
    
    // NEW: Spiritual/Religious templates
    if (category === "spiritual" || name.includes("spiritual") || name.includes("temple") || name.includes("church") || name.includes("prayer")) {
      return { type: "quote", text: "Faith is taking the first step even when you don't see the whole staircase.", author: "M.L. King Jr.", cta: null };
    }
    
    // NEW: Events & Celebrations templates
    if (category === "events_celebrations" || name.includes("diwali") || name.includes("christmas") || name.includes("festival") || name.includes("celebration")) {
      return { type: "event", title: "Happy Holidays!", date: "Celebrate Together", discount: "Special Wishes", cta: "Share Joy" };
    }
    
    // NEW: Nature templates
    if (category === "nature" || name.includes("nature") || name.includes("landscape") || name.includes("mountain") || name.includes("outdoor")) {
      return { type: "travel", title: "Explore Nature", price: "Breathtaking Views", feature: "Adventure Awaits", cta: "Discover" };
    }
    
    // Product/E-commerce templates
    if (name.includes("product") || name.includes("shop") || name.includes("sale") || category === "product") {
      return { type: "product", name: "New Collection", price: "$79", oldPrice: "$129", badge: "NEW", cta: "Shop Now" };
    }
    if (name.includes("menu") || name.includes("food") || name.includes("restaurant") || category === "restaurant") {
      return { type: "product", name: "Chef's Special", price: "$24", oldPrice: null, badge: "Popular", cta: "Order Now" };
    }
    
    // Property/Real Estate templates  
    if (name.includes("property") || name.includes("listing") || name.includes("real estate") || name.includes("home") || category === "property") {
      return { type: "property", price: "$750K", address: "456 Oak Street", beds: "3", baths: "2", sqft: "1,800", badge: "Open House" };
    }
    if (name.includes("rental") || name.includes("apartment")) {
      return { type: "property", price: "$2,400/mo", address: "Downtown Loft", beds: "2", baths: "1", sqft: "950", badge: "Available" };
    }
    
    // Service templates
    if (name.includes("service") || name.includes("consult") || name.includes("offer") || category === "service") {
      return { type: "service", title: "Book Today", subtitle: "Free Consultation", cta: "Learn More" };
    }
    if (name.includes("fitness") || name.includes("workout") || name.includes("gym") || category === "health") {
      return { type: "service", title: "Transform Your Body", subtitle: "Join Our Classes", cta: "Start Free" };
    }
    if (name.includes("spa") || name.includes("wellness") || name.includes("beauty")) {
      return { type: "service", title: "Relax & Rejuvenate", subtitle: "50% Off First Visit", cta: "Book Spa" };
    }
    
    // Event templates
    if (name.includes("event") || name.includes("announce") || name.includes("launch") || category === "event") {
      return { type: "event", title: "Grand Opening", date: "This Saturday", discount: "Free Entry", cta: "RSVP Now" };
    }
    if (name.includes("hiring") || name.includes("job") || name.includes("career") || category === "job") {
      return { type: "event", title: "We're Hiring!", date: "Apply Today", discount: "Join Us", cta: "Apply" };
    }
    if (name.includes("webinar") || name.includes("workshop") || name.includes("class") || category === "education") {
      return { type: "event", title: "Free Webinar", date: "Register Now", discount: "Limited Spots", cta: "Enroll" };
    }
    
    // Testimonial/Review templates
    if (name.includes("testimonial") || name.includes("review") || name.includes("customer")) {
      return { type: "testimonial", text: "Absolutely amazing experience! Highly recommend.", name: "John D.", role: "Happy Customer" };
    }
    
    // Automotive templates
    if (name.includes("auto") || name.includes("car") || name.includes("vehicle") || category === "automotive") {
      return { type: "auto", title: "2024 Model", price: "From $35K", feature: "Test Drive Today", cta: "View Details" };
    }
    
    // Travel templates
    if (name.includes("travel") || name.includes("vacation") || name.includes("trip") || category === "travel") {
      return { type: "travel", title: "Paradise Awaits", price: "From $999", feature: "All Inclusive", cta: "Book Trip" };
    }
    
    // Pet templates
    if (name.includes("pet") || name.includes("dog") || name.includes("cat") || category === "pet") {
      return { type: "pet", title: "Adopt Me!", subtitle: "Give a Forever Home", cta: "Meet Pets" };
    }
    
    // Default based on layout
    if (template.layout === "banner") {
      return { type: "event", title: "Special Offer", date: "Limited Time", discount: "Save Big", cta: "Learn More" };
    }
    if (template.layout === "centered_text") {
      return { type: "quote", text: "Your vision becomes clear when you look inside.", author: "C. Jung", cta: null };
    }
    
    // Fallback
    return { type: "service", title: "Your Title Here", subtitle: "Your Message", cta: "Take Action" };
  };
  
  const sample = getSampleContent();
  
  return (
    <div 
      className="relative w-full aspect-square rounded-xl overflow-hidden group"
      style={{ ...bgStyle, boxShadow: '0 4px 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)' }}
    >
      {/* Premium glass overlay effect */}
      <div className="absolute inset-0 opacity-20" style={{
        background: `radial-gradient(ellipse at 30% 20%, rgba(255,255,255,0.3) 0%, transparent 50%), 
                     radial-gradient(ellipse at 70% 80%, ${accentColor}40 0%, transparent 40%)`
      }} />
      
      {/* Subtle grid pattern for premium feel */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `linear-gradient(${isLight ? '#000' : '#fff'} 1px, transparent 1px), linear-gradient(90deg, ${isLight ? '#000' : '#fff'} 1px, transparent 1px)`,
        backgroundSize: '20px 20px'
      }} />
      
      {/* "Sample" indicator - top left */}
      <div className="absolute top-1 left-1 z-10 px-1.5 py-0.5 bg-black/50 backdrop-blur-sm rounded text-[5px] text-white/70 font-medium">
        Sample
      </div>
      
      {/* Content based on layout - with CATEGORY-SPECIFIC SAMPLE TEXT */}
      <div className="absolute inset-0 p-3 pt-5 flex flex-col">
        
        {/* QUOTE layout */}
        {(template.layout === "centered_text" && sample.type === "quote") && (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-2">
            <div className={`text-3xl font-serif leading-none ${isLight ? 'text-gray-300' : 'text-white/25'} -mb-1`}>"</div>
            <p className={`text-[8px] font-medium leading-tight ${isLight ? 'text-gray-700' : 'text-white/90'} mb-2`} style={{
              textShadow: isLight ? 'none' : '0 1px 2px rgba(0,0,0,0.3)'
            }}>{sample.text}</p>
            <div className={`w-8 h-[2px] ${isLight ? 'bg-gray-300' : 'bg-white/30'} rounded-full mb-1.5`} />
            <p className={`text-[6px] tracking-wider uppercase ${isLight ? 'text-gray-500' : 'text-white/50'}`}>— {sample.author}</p>
          </div>
        )}
        
        {/* PRODUCT layout */}
        {(template.layout === "product_centered" || sample.type === "product") && template.layout !== "centered_text" && (
          <>
            <div className="absolute top-5 right-2 px-2 py-0.5 bg-red-500 text-white text-[6px] font-bold rounded-full shadow-lg">{sample.badge || "NEW"}</div>
            <div className="flex-1 flex items-center justify-center">
              <div className={`relative w-16 h-16 ${isLight ? 'bg-white' : 'bg-white/10 backdrop-blur-sm'} rounded-xl flex items-center justify-center transform rotate-3 shadow-xl`}>
                <ImageIcon className={`w-8 h-8 ${isLight ? 'text-gray-300' : 'text-white/40'}`} />
              </div>
            </div>
            <div className="mt-auto text-center">
              <p className={`text-[8px] font-semibold ${isLight ? 'text-gray-800' : 'text-white/90'} mb-0.5`}>{sample.name}</p>
              <div className="flex items-center justify-center gap-1.5">
                <span className="text-[9px] font-bold text-green-500">{sample.price}</span>
                {sample.oldPrice && <span className={`text-[6px] line-through ${isLight ? 'text-gray-400' : 'text-white/40'}`}>{sample.oldPrice}</span>}
              </div>
              {sample.cta && <p className={`text-[6px] mt-1 ${isLight ? 'text-blue-600' : 'text-blue-400'}`}>{sample.cta} →</p>}
            </div>
          </>
        )}
        
        {/* PROPERTY/LISTING layout */}
        {(template.layout === "listing_card" || sample.type === "property") && template.layout !== "product_centered" && template.layout !== "centered_text" && (
          <>
            <div className={`flex-1 ${isLight ? 'bg-gray-100' : 'bg-white/10'} rounded-lg flex items-center justify-center relative overflow-hidden`}>
              <ImageIcon className={`w-8 h-8 ${isLight ? 'text-gray-300' : 'text-white/30'}`} />
              <div className="absolute top-1 left-1 px-1.5 py-0.5 bg-blue-500 text-white text-[5px] font-bold rounded shadow">{sample.badge}</div>
            </div>
            <div className="mt-1.5 space-y-0.5">
              <p className={`text-[10px] font-bold ${isLight ? 'text-gray-900' : 'text-white'}`}>{sample.price}</p>
              <div className="flex items-center gap-0.5">
                <MapPin className={`w-2 h-2 ${isLight ? 'text-gray-400' : 'text-white/50'}`} />
                <p className={`text-[6px] ${isLight ? 'text-gray-600' : 'text-white/70'}`}>{sample.address}</p>
              </div>
              <div className="flex gap-1.5 pt-0.5">
                <span className={`text-[6px] ${isLight ? 'text-gray-500' : 'text-white/60'}`}><b>{sample.beds}</b> bed</span>
                <span className={`text-[6px] ${isLight ? 'text-gray-500' : 'text-white/60'}`}><b>{sample.baths}</b> bath</span>
                <span className={`text-[6px] ${isLight ? 'text-gray-500' : 'text-white/60'}`}><b>{sample.sqft}</b> sqft</span>
              </div>
            </div>
          </>
        )}
        
        {/* BANNER/EVENT layout */}
        {(template.layout === "banner" || sample.type === "event") && template.layout !== "listing_card" && template.layout !== "product_centered" && template.layout !== "centered_text" && (
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className={`w-28 h-28 rounded-full ${isLight ? 'bg-yellow-100' : 'bg-white/5'} blur-2xl`} />
            </div>
            <div className={`px-2.5 py-0.5 ${isLight ? 'bg-yellow-400 text-yellow-900' : 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white'} text-[7px] font-black rounded-full mb-1.5 shadow-lg transform -rotate-2`}>
              {sample.discount}
            </div>
            <p className={`text-[11px] font-black tracking-tight ${isLight ? 'text-gray-900' : 'text-white'} mb-0.5`} style={{
              textShadow: isLight ? 'none' : '0 2px 4px rgba(0,0,0,0.4)'
            }}>{sample.title}</p>
            <p className={`text-[7px] ${isLight ? 'text-gray-600' : 'text-white/70'} mb-2`}>{sample.date}</p>
            <div className={`px-3 py-1 ${isLight ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'} text-[6px] font-bold rounded-full shadow-lg`}>{sample.cta}</div>
          </div>
        )}
        
        {/* TESTIMONIAL layout */}
        {(template.layout === "quote_card" || sample.type === "testimonial") && template.layout !== "banner" && (
          <>
            {template.show_stars && (
              <div className="flex gap-0.5 mb-1.5">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} className="w-2.5 h-2.5 text-yellow-400 fill-yellow-400 drop-shadow" />
                ))}
              </div>
            )}
            <div className="flex-1 flex flex-col justify-center">
              <p className={`text-[7px] leading-relaxed ${isLight ? 'text-gray-700' : 'text-white/85'} italic`}>
                "{sample.text}"
              </p>
            </div>
            {template.show_avatar && (
              <div className="flex items-center gap-1.5 mt-1.5 pt-1.5 border-t border-white/10">
                <div className={`w-6 h-6 rounded-full ${isLight ? 'bg-gray-200' : 'bg-gradient-to-br from-white/30 to-white/10'} flex items-center justify-center shadow-inner`}>
                  <User className={`w-3 h-3 ${isLight ? 'text-gray-400' : 'text-white/60'}`} />
                </div>
                <div>
                  <p className={`text-[7px] font-semibold ${isLight ? 'text-gray-800' : 'text-white/90'}`}>{sample.name}</p>
                  <p className={`text-[5px] ${isLight ? 'text-gray-500' : 'text-white/50'}`}>{sample.role}</p>
                </div>
              </div>
            )}
          </>
        )}
        
        {/* SERVICE layout */}
        {(template.layout === "bottom_text" || sample.type === "service") && template.layout !== "quote_card" && template.layout !== "banner" && template.layout !== "listing_card" && template.layout !== "product_centered" && template.layout !== "centered_text" && (
          <>
            <div className="flex-1 flex items-center justify-center">
              <div className={`w-14 h-14 rounded-xl ${isLight ? 'bg-gray-100' : 'bg-white/10'} flex items-center justify-center shadow-lg`}>
                <Sparkles className={`w-7 h-7 ${isLight ? 'text-purple-400' : 'text-white/50'}`} />
              </div>
            </div>
            <div className="mt-auto text-center pt-1.5">
              <p className={`text-[9px] font-bold ${isLight ? 'text-gray-900' : 'text-white'}`}>{sample.title}</p>
              <p className={`text-[6px] ${isLight ? 'text-gray-500' : 'text-white/60'}`}>{sample.subtitle}</p>
              {sample.cta && <p className={`text-[6px] mt-1 ${isLight ? 'text-blue-600' : 'text-blue-400'}`}>{sample.cta} →</p>}
            </div>
          </>
        )}
        
        {/* VIDEO PROMO layout */}
        {template.layout === "video_promo" && (
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 shadow-xl`} style={{
              background: isLight ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.05) 100%)',
              boxShadow: `0 0 25px ${isLight ? '#667eea50' : accentColor + '40'}`
            }}>
              <div className="w-0 h-0 border-t-[6px] border-t-transparent border-l-[10px] border-l-white border-b-[6px] border-b-transparent ml-0.5" />
            </div>
            <p className={`text-[8px] font-bold ${isLight ? 'text-gray-800' : 'text-white/90'} mb-0.5`}>Watch Video</p>
            <p className={`text-[6px] ${isLight ? 'text-gray-500' : 'text-white/50'}`}>Play Now</p>
          </div>
        )}
        
        {/* TOP TEXT layout */}
        {template.layout === "top_text" && (
          <>
            <div className="text-center mb-2">
              <p className={`text-[9px] font-bold ${isLight ? 'text-gray-900' : 'text-white'}`}>{sample.title || "New Arrival"}</p>
              <p className={`text-[6px] ${isLight ? 'text-gray-500' : 'text-white/60'}`}>{sample.subtitle || "Limited Edition"}</p>
            </div>
            <div className="flex-1 flex items-center justify-center">
              <div className={`w-16 h-16 rounded-lg ${isLight ? 'bg-gray-100' : 'bg-white/10'} flex items-center justify-center shadow-lg transform -rotate-3`}>
                <ImageIcon className={`w-8 h-8 ${isLight ? 'text-gray-300' : 'text-white/30'}`} />
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Premium Viraly Watermark */}
      <div className="absolute bottom-1 right-1 px-1.5 py-0.5 bg-black/70 backdrop-blur-md rounded flex items-center gap-0.5 shadow-lg" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
        <span className="text-[6px] font-black text-viraly-orange">V</span>
        <span className="text-[5px] font-semibold text-white/90 tracking-wide">iraly</span>
      </div>
    </div>
  );
}

export default function TemplateSelector({ 
  isOpen, 
  onClose, 
  onSelect, 
  type = "image",
  selectedTemplate = null,
  multiSelect = false,
  selectedTemplates = [],
  suggestedCategory = null,
  suggestedTemplates = []
}) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [hoveredTemplate, setHoveredTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [hasAutoSelected, setHasAutoSelected] = useState(false);
  
  // Track locally selected templates for multi-select mode
  const [localSelected, setLocalSelected] = useState(selectedTemplates || []);
  
  // Saved/Favorite templates
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [showSavedOnly, setShowSavedOnly] = useState(false);
  
  // Load saved templates from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('viraly_saved_templates');
      if (saved) {
        setSavedTemplates(JSON.parse(saved));
      }
    } catch (e) {
      console.error('Failed to load saved templates:', e);
    }
  }, []);
  
  // Save template to favorites
  const toggleSaveTemplate = (template, e) => {
    e?.stopPropagation();
    const isSaved = savedTemplates.some(t => t.id === template.id);
    let newSaved;
    
    if (isSaved) {
      newSaved = savedTemplates.filter(t => t.id !== template.id);
      toast.success('Removed from favorites');
    } else {
      newSaved = [...savedTemplates, { 
        id: template.id, 
        name: template.name, 
        category: template.category,
        background_style: template.background_style,
        layout: template.layout
      }];
      toast.success('Saved to favorites!');
    }
    
    setSavedTemplates(newSaved);
    localStorage.setItem('viraly_saved_templates', JSON.stringify(newSaved));
  };
  
  // Check if template is saved
  const isTemplateSaved = (templateId) => {
    return savedTemplates.some(t => t.id === templateId);
  };
  
  // Sync localSelected when selectedTemplates prop changes
  useEffect(() => {
    setLocalSelected(selectedTemplates || []);
  }, [selectedTemplates]);
  
  // Auto-select suggested category when modal opens with a suggestion
  useEffect(() => {
    if (isOpen && suggestedCategory && !hasAutoSelected) {
      // Map detected category to template category (only for categories that don't exist directly)
      const categoryMap = {
        "real_estate": "property",
        "accounting": "finance",
        "yoga_wellness": "fitness",
        "creative": "general",
        "coaching": "service",
        "consulting": "service",
        "home_services": "construction"
      };
      
      // Use direct mapping if the category exists, otherwise fall back to map
      const mappedCategory = categories.find(c => c.id === suggestedCategory) 
        ? suggestedCategory 
        : (categoryMap[suggestedCategory] || suggestedCategory);
      
      // Check if the mapped category exists in our categories list
      const validCategory = categories.find(c => c.id === mappedCategory);
      if (validCategory) {
        setSelectedCategory(mappedCategory);
        setHasAutoSelected(true);
      }
    }
    
    // Reset auto-select flag when modal closes
    if (!isOpen) {
      setHasAutoSelected(false);
    }
  }, [isOpen, suggestedCategory, hasAutoSelected]);
  
  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
    }
  }, [isOpen, type, selectedCategory]);
  
  const fetchTemplates = async () => {
    setLoading(true);
    try {
      let url = `/settings/templates?type=${type}`;
      if (selectedCategory && selectedCategory !== "all") {
        url += `&category=${selectedCategory}`;
      }
      const response = await apiClient.get(url);
      setTemplates(response.data);
    } catch (error) {
      console.error("Failed to fetch templates:", error);
      setTemplates([]);
    }
    setLoading(false);
  };
  
  // Get templates to display (all or saved only)
  const displayedTemplates = showSavedOnly 
    ? templates.filter(t => savedTemplates.some(s => s.id === t.id))
    : templates;
  
  // Handle template click (for multi-select or single-select)
  const handleTemplateClick = (template) => {
    if (multiSelect) {
      // Multi-select mode: toggle selection
      const isSelected = localSelected.some(t => t.id === template.id);
      if (isSelected) {
        setLocalSelected(localSelected.filter(t => t.id !== template.id));
      } else {
        setLocalSelected([...localSelected, template]);
      }
      // Call onSelect to notify parent of each selection change
      onSelect(template);
    } else {
      // Single-select: just call onSelect and close
      onSelect(template);
    }
  };
  
  // Check if template is selected
  const isTemplateSelected = (template) => {
    if (multiSelect) {
      return localSelected.some(t => t.id === template.id);
    }
    return selectedTemplate?.id === template.id;
  };
  
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="relative w-full max-w-5xl max-h-[85vh] bg-viraly-dark border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <div>
              <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
                <Layout className="w-6 h-6 text-viraly-orange" />
                Choose a Template
              </h2>
              <p className="text-sm text-viraly-muted mt-1">
                Select a pre-designed layout. <span className="text-yellow-400">Note: Personal branding is not applied when using templates.</span>
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Category Tabs - Scrollable with icons */}
          <div className="flex gap-2 p-4 border-b border-white/10 overflow-x-auto scrollbar-thin">
            {/* My Saved Button */}
            <button
              onClick={() => setShowSavedOnly(!showSavedOnly)}
              className={`px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all flex items-center gap-1.5 ${
                showSavedOnly
                  ? "bg-pink-500 text-white"
                  : "bg-pink-500/10 text-pink-400 hover:bg-pink-500/20 border border-pink-500/30"
              }`}
            >
              <Heart className={`w-4 h-4 ${showSavedOnly ? 'fill-current' : ''}`} />
              <span>My Saved</span>
              {savedTemplates.length > 0 && (
                <span className="text-xs opacity-70">({savedTemplates.length})</span>
              )}
            </button>
            
            <div className="w-px bg-white/20 mx-1" />
            
            {categories.map((cat) => {
              // Check if this category is suggested by AI
              const isSuggested = suggestedTemplates?.includes(cat.id) || 
                (suggestedCategory && (cat.id === suggestedCategory || 
                  (suggestedCategory === "mortgage" && cat.id === "property") ||
                  (suggestedCategory === "real_estate" && cat.id === "property") ||
                  (suggestedCategory === "finance" && cat.id === "business")));
              
              return (
                <button
                  key={cat.id}
                  onClick={() => {
                    setSelectedCategory(cat.id);
                    setShowSavedOnly(false);
                  }}
                  className={`px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all flex items-center gap-1.5 ${
                    selectedCategory === cat.id && !showSavedOnly
                      ? "bg-viraly-orange text-white"
                      : isSuggested
                        ? "bg-viraly-orange/20 text-viraly-orange border border-viraly-orange/50 hover:bg-viraly-orange/30"
                        : "bg-white/5 text-viraly-muted hover:bg-white/10"
                  }`}
                >
                  <span>{cat.icon}</span>
                  <span>{cat.label}</span>
                  {isSuggested && selectedCategory !== cat.id && (
                    <span className="text-[10px] bg-viraly-orange/30 px-1.5 py-0.5 rounded-full">AI</span>
                  )}
                  {cat.id !== "all" && <span className="text-xs opacity-70">({cat.count})</span>}
                </button>
              );
            })}
          </div>
          
          {/* Free Templates Banner */}
          <div className="mx-4 mt-4 p-3 rounded-lg bg-green-500/10 border border-green-500/30">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm text-green-400 flex items-center gap-2">
                  <span className="text-lg">🎁</span>
                  <span><strong>500 FREE templates</strong> included with every plan!</span>
                </p>
                <p className="text-xs text-viraly-muted flex items-center gap-2">
                  <span>💡</span>
                  <span>Sample text shown • <strong>You customize with your content</strong> • Drag fields to any position in the template</span>
                </p>
              </div>
              {multiSelect && localSelected.length > 0 && (
                <span className="text-sm font-bold text-viraly-orange">
                  {localSelected.length} selected
                </span>
              )}
            </div>
          </div>
          
          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[50vh]">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-viraly-orange" />
              </div>
            ) : showSavedOnly && savedTemplates.length === 0 ? (
              /* Empty state for saved templates */
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Heart className="w-12 h-12 text-pink-400/30 mb-4" />
                <h3 className="font-heading text-lg font-bold mb-2">No Saved Templates Yet</h3>
                <p className="text-sm text-viraly-muted mb-4 max-w-md">
                  Click the heart icon on any template to save it to your favorites for quick access later.
                </p>
                <Button
                  variant="outline"
                  onClick={() => setShowSavedOnly(false)}
                  className="text-pink-400 border-pink-400/30"
                >
                  Browse All Templates
                </Button>
              </div>
            ) : showSavedOnly && displayedTemplates.length === 0 ? (
              /* Templates were saved but filtered out by category */
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Heart className="w-12 h-12 text-pink-400/30 mb-4" />
                <h3 className="font-heading text-lg font-bold mb-2">No Saved Templates in This Category</h3>
                <p className="text-sm text-viraly-muted mb-4">
                  Your saved templates are in other categories.
                </p>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSelectedCategory("all");
                  }}
                  className="text-pink-400 border-pink-400/30"
                >
                  Show All Saved
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {/* AI Generated Option - Only show in single-select mode and not in saved view */}
                {!multiSelect && !showSavedOnly && (
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className={`relative rounded-xl border-2 border-dashed cursor-pointer overflow-hidden transition-all ${
                      selectedTemplate === null
                        ? "border-viraly-orange bg-viraly-orange/10"
                        : "border-white/20 hover:border-white/40 bg-white/5"
                    }`}
                    onClick={() => onSelect(null)}
                  >
                    <div className="aspect-square flex flex-col items-center justify-center p-3 text-center">
                      <div className="w-12 h-12 rounded-full bg-viraly-gradient flex items-center justify-center mb-3">
                        <Sparkles className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="font-heading font-bold text-sm">Viraly AI</h3>
                      <p className="text-[10px] text-viraly-muted mt-1">
                        Unique design + your personal branding
                      </p>
                      {selectedTemplate === null && (
                        <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-viraly-orange flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
                
                {/* Template Cards */}
                {displayedTemplates.map((template) => {
                  const isSelected = isTemplateSelected(template);
                  const isSaved = isTemplateSaved(template.id);
                  return (
                    <motion.div
                      key={template.id}
                      whileHover={{ scale: 1.02 }}
                      className={`relative rounded-xl border-2 cursor-pointer overflow-hidden transition-all ${
                        isSelected
                          ? "border-viraly-orange bg-viraly-orange/10"
                          : "border-white/10 hover:border-white/30 bg-white/5"
                      }`}
                      onClick={() => handleTemplateClick(template)}
                      onMouseEnter={() => setHoveredTemplate(template.id)}
                      onMouseLeave={() => setHoveredTemplate(null)}
                    >
                      {/* Save to favorites button - only shows on hover */}
                      <button
                        onClick={(e) => toggleSaveTemplate(template, e)}
                        className={`absolute top-2 left-2 z-20 w-6 h-6 rounded-full flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 ${
                          isSaved 
                            ? "bg-pink-500 text-white opacity-100" 
                            : "bg-black/60 text-white/80 hover:text-pink-400 hover:bg-black/80"
                        }`}
                        title={isSaved ? "Remove from saved" : "Save for later"}
                      >
                        <Heart className={`w-3.5 h-3.5 ${isSaved ? 'fill-current' : ''}`} />
                      </button>
                      
                      {/* Selection indicator for multi-select */}
                      {multiSelect && (
                        <div className={`absolute top-2 right-2 z-10 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                          isSelected 
                            ? "bg-viraly-orange border-viraly-orange" 
                            : "bg-white/10 border-white/30"
                        }`}>
                          {isSelected && <Check className="w-4 h-4 text-white" />}
                        </div>
                      )}
                      
                      {/* Template Preview */}
                      <TemplatePreview template={template} showDetails={hoveredTemplate === template.id} />
                    
                    {/* Hover Overlay */}
                    <AnimatePresence>
                      {hoveredTemplate === template.id && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center p-2"
                        >
                          <Button 
                            size="sm" 
                            className={`text-xs mb-2 ${isSelected ? 'bg-red-500 hover:bg-red-600' : 'btn-primary'}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTemplateClick(template);
                              if (!multiSelect) {
                                onClose();
                              }
                            }}
                          >
                            {multiSelect 
                              ? (isSelected ? 'Remove' : 'Add to Selection') 
                              : 'Use Template'
                            }
                          </Button>
                          <button
                            className="text-[10px] text-white/70 hover:text-white flex items-center gap-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewTemplate(template);
                            }}
                          >
                            <Eye className="w-3 h-3" />
                            Preview Layout
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                    
                    {/* Selected Indicator (only for single-select mode) */}
                    {!multiSelect && isSelected && (
                      <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-viraly-orange flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                    
                    {/* Template Name */}
                    <div className="p-2 border-t border-white/10">
                      <h4 className="font-medium text-xs truncate">{template.name}</h4>
                    </div>
                  </motion.div>
                  );
                })}
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="flex items-center justify-between p-4 border-t border-white/10 bg-white/5">
            <div className="flex items-center gap-4">
              <p className="text-sm text-viraly-muted">
                {multiSelect 
                  ? (localSelected.length > 0 
                      ? `${localSelected.length} template(s) selected` 
                      : "Select templates for bulk creation")
                  : (selectedTemplate 
                      ? `Selected: ${selectedTemplate.name}` 
                      : "Using Viraly AI + Personal Branding")
                }
              </p>
              <div className="flex items-center gap-2 text-xs text-viraly-muted">
                <div className="px-2 py-0.5 bg-viraly-orange/20 rounded text-viraly-orange font-bold">V</div>
                <span>Viraly watermark appears on all content</span>
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" size="sm" onClick={onClose}>
                {multiSelect ? 'Done' : 'Cancel'}
              </Button>
              {!multiSelect && (
                <Button 
                  className="btn-primary"
                  size="sm"
                  onClick={() => {
                    onClose();
                  }}
                >
                  Continue
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </div>
        </motion.div>
        
        {/* Template Preview Modal */}
        <AnimatePresence>
          {previewTemplate && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-60 flex items-center justify-center bg-black/80"
              onClick={() => setPreviewTemplate(null)}
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="bg-viraly-dark border border-white/20 rounded-2xl p-6 max-w-md w-full mx-4"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="font-heading text-xl font-bold mb-4">{previewTemplate.name}</h3>
                
                <div className="mb-4">
                  <TemplatePreview template={previewTemplate} showDetails={true} />
                </div>
                
                <p className="text-sm text-viraly-muted mb-4">{previewTemplate.description}</p>
                
                {/* Template Features */}
                <div className="space-y-2 mb-4">
                  <h4 className="text-xs font-semibold text-viraly-muted uppercase">Template Features</h4>
                  <div className="flex flex-wrap gap-2">
                    <div className="flex items-center gap-1 px-2 py-1 bg-white/10 rounded text-xs">
                      <Palette className="w-3 h-3" />
                      {previewTemplate.background_style?.type === "gradient" ? "Gradient BG" : "Solid BG"}
                    </div>
                    <div className="flex items-center gap-1 px-2 py-1 bg-white/10 rounded text-xs">
                      <Type className="w-3 h-3" />
                      {previewTemplate.text_style?.font || "Sans-serif"}
                    </div>
                    {previewTemplate.price_overlay && (
                      <div className="flex items-center gap-1 px-2 py-1 bg-white/10 rounded text-xs">
                        <Tag className="w-3 h-3" />
                        Price Tag
                      </div>
                    )}
                    {previewTemplate.show_stars && (
                      <div className="flex items-center gap-1 px-2 py-1 bg-white/10 rounded text-xs">
                        <Star className="w-3 h-3" />
                        Star Rating
                      </div>
                    )}
                    {previewTemplate.show_avatar && (
                      <div className="flex items-center gap-1 px-2 py-1 bg-white/10 rounded text-xs">
                        <User className="w-3 h-3" />
                        Avatar
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Viraly Watermark Note */}
                <div className="p-3 bg-viraly-orange/10 border border-viraly-orange/30 rounded-lg mb-4">
                  <p className="text-xs text-viraly-orange">
                    <strong>Note:</strong> Viraly watermark is automatically added to all content in the bottom-right corner.
                  </p>
                </div>
                
                <div className="flex gap-3">
                  <Button variant="outline" className="flex-1" onClick={() => setPreviewTemplate(null)}>
                    Back
                  </Button>
                  <Button 
                    className="btn-primary flex-1"
                    onClick={() => {
                      onSelect(previewTemplate);
                      setPreviewTemplate(null);
                      onClose();
                    }}
                  >
                    Use This Template
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
}
